import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import Seo from '../../components/Seo'
import { useSession } from '../../api/session'

export default function Login() {
  const { login, loginWithCode } = useSession()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<'email' | 'local' | 'unavailable' | null>(null)
  const [requested, setRequested] = useState(false)
  const [email, setEmail] = useState('')
  const magicStarted = useRef(false)
  useEffect(() => {
    api.authOptions().then(x => setMode(x.mode)).catch(e => setError(e.message))
    const token = new URLSearchParams(window.location.search).get('t')
    if (token && !magicStarted.current) {
      magicStarted.current = true
      setBusy(true)
      loginWithCode('', '', token).then(() => navigate('/app', { replace: true }))
        .catch(e => setError(e.message)).finally(() => setBusy(false))
    }
  }, [])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setBusy(true)
    setError(null)
    try {
      if (mode === 'email' && !requested) {
        await api.requestCode(email); setRequested(true); return
      }
      if (mode === 'email') await loginWithCode(email, String(form.get('code') ?? ''))
      else await login(email, String(form.get('password') ?? ''), String(form.get('code') ?? ''))
      navigate(location.state?.from ?? '/app', { replace: true })
    } catch (err) {
      setError(err instanceof Error && err.message !== 'Unauthorized' ? err.message : 'Email or password did not match.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Seo title="Analyst login" noindex />
      <section className="hero">
        <span className="eyebrow">Analysts</span>
        <h1 style={{ marginTop: '0.75rem' }}>Sign in</h1>
        <p className="lede">Access to engagement records is limited to Ledgerline analysts.</p>
      </section>
      <section className="section">
        <form className="form" onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" name="email" type="email" className="input" autoComplete="username" value={email} onChange={e => { setEmail(e.target.value); setRequested(false) }} required />
          </div>
          {mode === 'local' && <div className="field">
            <label htmlFor="password">Password</label>
            <input id="password" name="password" type="password" className="input" autoComplete="current-password" required />
          </div>}
          {(mode === 'local' || requested) && <div className="field"><label htmlFor="code">{mode === 'email' ? 'Email passcode' : 'Authenticator code (if enabled)'}</label><input id="code" name="code" className="input" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" required={mode === 'email'} /></div>}
          {requested && <p className="form-note">If your email is eligible, a sign-in code is on its way. The code expires in 10 minutes.</p>}
          {mode === 'unavailable' && <p className="notice">Sign-in is being configured. Please contact your administrator.</p>}
          {error && (
            <div className="notice crit" role="alert">
              {error}
            </div>
          )}
          <div>
            <button className="btn" type="submit" disabled={busy || !mode || mode === 'unavailable'}>
              {busy ? 'Please wait…' : mode === 'email' && !requested ? 'Email me a code' : 'Sign in'}
            </button>
          </div>
        </form>
      </section>
    </>
  )
}
